import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { Autocomplete, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import LoadingSpinner from "components/LoadingSpinner";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import { MecordColors } from "Constants/Color";
import { RootState } from "../../../../index";
import {
    createAccessoryReturn,
    fetchAccessoryReturnData,
} from "slices/sales_order/challan/thunk";
import { clearChallanState } from "slices/sales_order/challan/reducer";

type ReturnUnitOption = {
    inventory_product_id: string;
    serial_no: string;
    warranty_date?: string | null;
};

type FlatRow = {
    key: string;           // accessory composite key
    unitIdx: number;       // 0-based index within this accessory's units
    globalIdx: number;     // overall S.No across all accessories
    checked: boolean;
    inventory_product_id: string;
    serial_no: string;
    warranty_date?: string | null;
    // display info
    product_name: string;
    product_sku: string;
    service_name: string;
    service_sku: string;
    service_sn: string;
    issued_qty: number;
    already_returned_qty: number;
    remaining_qty: number;
    available: ReturnUnitOption[];
};

const accessoryKey = (itemId: string, accessory: any) =>
    `${itemId}::${accessory?.accessory_id || accessory?.product_id || ""}`;

const ReturnAccessories: React.FC = () => {
    document.title = "Return Accessories";

    const { challanId } = useParams<{ challanId: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch<any>();
    const { accessoryReturnData, loading } = useSelector((state: RootState) => state.Challan);

    const [flatRows, setFlatRows] = useState<FlatRow[]>([]);
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        setInitialized(false);
        setFlatRows([]);
        setNote("");
        if (challanId) {
            dispatch(fetchAccessoryReturnData(challanId));
        }
        return () => {
            dispatch(clearChallanState());
        };
    }, [dispatch, challanId]);

    // Build flat rows from API data
    useEffect(() => {
        if (!accessoryReturnData || initialized) return;
        const rows: FlatRow[] = [];
        let globalIdx = 0;
        (accessoryReturnData.services || []).forEach((service: any) => {
            (service.accessories || []).forEach((acc: any) => {
                const key = accessoryKey(service.sales_order_service_item_id, acc);
                const remaining = Math.max(0, Number(acc.remaining_qty || 0));
                const available: ReturnUnitOption[] = Array.isArray(acc.available_return_units)
                    ? acc.available_return_units
                    : [];
                for (let i = 0; i < remaining; i++) {
                    rows.push({
                        key,
                        unitIdx: i,
                        globalIdx: ++globalIdx,
                        checked: false,
                        inventory_product_id: "",
                        serial_no: "",
                        warranty_date: null,
                        product_name: acc.product_name || "-",
                        product_sku: acc.product_sku || "",
                        service_name: service.product_name || "Service",
                        service_sku: service.product_sku || "",
                        service_sn: service.serial_number || "",
                        issued_qty: Number(acc.issued_qty || 0),
                        already_returned_qty: Number(acc.already_returned_qty || 0),
                        remaining_qty: remaining,
                        available,
                    });
                }
            });
        });
        setFlatRows(rows);
        setInitialized(true);
    }, [accessoryReturnData, initialized]);

    const listingPath = accessoryReturnData?.sales_order_service_id
        ? `/sales_order/service/challan/${accessoryReturnData.sales_order_service_id}`
        : "/sales_order/service/sales_order_log";

    const thStyle: React.CSSProperties = {
        padding: "10px 12px",
        borderBottom: "2px solid #eee",
        textAlign: "left",
        fontSize: "0.78rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        backgroundColor: MecordColors.LightGrayColor,
    };
    const tdStyle: React.CSSProperties = {
        padding: "10px 12px",
        borderBottom: "1px solid #f0f0f0",
        fontSize: "0.78rem",
        verticalAlign: "middle",
    };

    const summary = accessoryReturnData?.accessory_return_summary || {};
    const canReturn = !!summary.can_return;

    const totalReturnQty = useMemo(
        () => flatRows.filter((r) => r.checked && r.inventory_product_id).length,
        [flatRows],
    );

    const updateRow = (globalIdx: number, patch: Partial<FlatRow>) => {
        setFlatRows((prev) =>
            prev.map((r) => (r.globalIdx === globalIdx ? { ...r, ...patch } : r)),
        );
    };

    // Get already-selected ids for an accessory key (to prevent duplicate selection)
    const selectedIdsForKey = (key: string, excludeGlobalIdx: number): Set<string> => {
        const ids = new Set<string>();
        flatRows.forEach((r) => {
            if (r.key === key && r.globalIdx !== excludeGlobalIdx && r.inventory_product_id) {
                ids.add(r.inventory_product_id);
            }
        });
        return ids;
    };

    const handleCheckAll = () => {
        setFlatRows((prev) => prev.map((r) => ({ ...r, checked: true })));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!challanId || submitting) return;

        // Validate: checked rows must have a serial
        for (const row of flatRows) {
            if (row.checked && !row.inventory_product_id) {
                toast.error(`Select a serial number for each checked unit of "${row.product_name}".`);
                return;
            }
        }

        // Check duplicate serials per accessory key
        const keyToIds: Record<string, string[]> = {};
        for (const row of flatRows) {
            if (row.checked && row.inventory_product_id) {
                if (!keyToIds[row.key]) keyToIds[row.key] = [];
                keyToIds[row.key].push(row.inventory_product_id);
            }
        }
        for (const [, ids] of Object.entries(keyToIds)) {
            if (new Set(ids).size !== ids.length) {
                toast.error("Duplicate serial selected for the same accessory.");
                return;
            }
        }

        // Build payload grouped by service item
        const services = accessoryReturnData?.services || [];
        const items = services
            .map((service: any) => ({
                sales_order_service_item_id: service.sales_order_service_item_id,
                accessories: (service.accessories || [])
                    .map((acc: any) => {
                        const key = accessoryKey(service.sales_order_service_item_id, acc);
                        const selectedIds = flatRows
                            .filter((r) => r.key === key && r.checked && r.inventory_product_id)
                            .map((r) => r.inventory_product_id);
                        if (!selectedIds.length) return null;
                        return {
                            accessory_id: acc.accessory_id,
                            product_id: acc.product_id,
                            return_qty: selectedIds.length,
                            return_inventory_product_ids: selectedIds,
                        };
                    })
                    .filter(Boolean),
            }))
            .filter((item: any) => item.accessories.length > 0);

        if (!items.length) {
            toast.error("Check at least one unit and select its serial number.");
            return;
        }

        setSubmitting(true);
        try {
            await dispatch(
                createAccessoryReturn({
                    challanId,
                    payload: { note: note.trim() || undefined, items },
                }),
            ).unwrap();
            toast.success("Accessory return saved successfully.");
            navigate(listingPath);
        } catch (error: any) {
            toast.error(
                typeof error === "string"
                    ? error
                    : error?.message || "Failed to save accessory return.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (loading && !accessoryReturnData) return <LoadingSpinner />;
    if (!accessoryReturnData) {
        return <div className="p-4 text-center">Challan not found.</div>;
    }

    return (
        <PageContainer>
            <PageBreadcrumb
                title="Return Accessories"
                titleLink=""
                breadcrumbs={[
                    { label: "Sales Order Services", to: "/sales_order/service/sales_order_log" },
                    { label: "Manage Challan", to: listingPath },
                    { label: "Return Accessories", to: "" },
                ]}
                backNavigationPath={listingPath}
            />
            <hr />

            <Row>
                <Col lg={12}>
                    <Card className="shadow-none">
                        <Card.Header>
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-2">
                                    <AssignmentReturnIcon style={{ color: MecordColors.ThemeBGColor }} />
                                    <h6 className="card-title mb-0">Return unused accessories</h6>
                                </div>
                                {canReturn && flatRows.length > 0 && (
                                    <button
                                        type="button"
                                        disabled={submitting}
                                        onClick={handleCheckAll}
                                        className="btn btn-sm d-flex align-items-center gap-1"
                                        style={{
                                            backgroundColor: MecordColors.LightThemeBGColor,
                                            color: MecordColors.ThemeBGColor,
                                            fontSize: "0.8rem",
                                            borderRadius: "6px",
                                            padding: "6px 14px",
                                            border: `1px solid ${MecordColors.ThemeBGColor}`,
                                        }}
                                    >
                                        Check All
                                    </button>
                                )}
                            </div>
                        </Card.Header>

                        <Card.Body>
                            {/* Meta Info */}
                            <Row className="mb-3">
                                <Col md={4}>
                                    <p className="mb-2">
                                        <strong>Challan No:</strong> {accessoryReturnData.challan_no || "-"}
                                    </p>
                                    <p className="mb-0">
                                        <strong>SO No:</strong> {accessoryReturnData.sales_order_number || "-"}
                                    </p>
                                </Col>
                                <Col md={4}>
                                    <p className="mb-2">
                                        <strong>Challan Date:</strong>{" "}
                                        <FormattedDate date={accessoryReturnData.challan_date} />
                                    </p>
                                    <p className="mb-0">
                                        <strong>Warehouse:</strong> {accessoryReturnData.warehouse_name || "-"}
                                    </p>
                                </Col>
                                <Col md={4}>
                                    <p className="mb-2">
                                        <strong>Issued Qty:</strong> {summary.issued_qty || 0}
                                    </p>
                                    <p className="mb-0">
                                        <strong>Already Returned:</strong> {summary.returned_qty || 0}{" "}
                                        <span className="text-muted">
                                            (Remaining {summary.remaining_qty || 0})
                                        </span>
                                    </p>
                                </Col>
                            </Row>

                            {!canReturn && (
                                <div
                                    className="mb-3"
                                    style={{
                                        backgroundColor: "#fff8e6",
                                        border: "1px solid #f3d9a4",
                                        borderRadius: 6,
                                        padding: "10px 12px",
                                        fontSize: "0.85rem",
                                    }}
                                >
                                    All issued accessories on this challan are already marked for return.
                                </div>
                            )}

                            <Form onSubmit={handleSubmit}>
                                {/* Flat table */}
                                <div className="table-responsive mb-4">
                                    {flatRows.length > 0 ? (
                                        <table
                                            style={{
                                                width: "100%",
                                                borderCollapse: "collapse",
                                                fontSize: "0.78rem",
                                            }}
                                        >
                                            <thead>
                                                <tr>
                                                    <th style={{ ...thStyle, textAlign: "center", width: 60 }}>Return</th>
                                                    <th style={thStyle}>S.No</th>
                                                    <th style={thStyle}>Accessory</th>
                                                    <th style={thStyle}>SKU</th>
                                                    <th style={thStyle}>Service Item</th>
                                                    <th style={{ ...thStyle, textAlign: "center" }}>Issued</th>
                                                    <th style={{ ...thStyle, textAlign: "center" }}>Returned</th>
                                                    <th style={{ ...thStyle, textAlign: "center" }}>Remaining</th>
                                                    <th style={{ ...thStyle, minWidth: 180 }}>Serial No</th>
                                                    <th style={{ ...thStyle, minWidth: 120 }}>Warranty</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {flatRows.map((row, idx) => {
                                                    const excluded = selectedIdsForKey(row.key, row.globalIdx);
                                                    const filteredOptions = row.available.filter(
                                                        (opt) =>
                                                            opt.inventory_product_id === row.inventory_product_id ||
                                                            !excluded.has(opt.inventory_product_id),
                                                    );
                                                    const selectedValue: ReturnUnitOption | null =
                                                        row.inventory_product_id
                                                        ? {
                                                              inventory_product_id: row.inventory_product_id,
                                                              serial_no:
                                                                  row.serial_no ||
                                                                  row.available.find(
                                                                      (o) =>
                                                                          o.inventory_product_id ===
                                                                          row.inventory_product_id,
                                                                  )?.serial_no ||
                                                                  "",
                                                              warranty_date:
                                                                  row.warranty_date ||
                                                                  row.available.find(
                                                                      (o) =>
                                                                          o.inventory_product_id ===
                                                                          row.inventory_product_id,
                                                                  )?.warranty_date ||
                                                                  null,
                                                          }
                                                        : null;

                                                    return (
                                                        <tr
                                                            key={`${row.key}-u-${row.unitIdx}`}
                                                            style={{
                                                                backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa",
                                                            }}
                                                        >
                                                            <td style={{ ...tdStyle, textAlign: "center", width: 60 }}>
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={row.checked}
                                                                    disabled={!canReturn || submitting}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked;
                                                                        updateRow(
                                                                            row.globalIdx,
                                                                            checked
                                                                                ? { checked: true }
                                                                                : {
                                                                                      checked: false,
                                                                                      inventory_product_id: "",
                                                                                      serial_no: "",
                                                                                      warranty_date: null,
                                                                                  },
                                                                        );
                                                                    }}
                                                                />
                                                            </td>
                                                            <td style={tdStyle}>{row.globalIdx}</td>
                                                            <td style={tdStyle}>
                                                                <span
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        color: MecordColors.ThemeBGColor,
                                                                    }}
                                                                >
                                                                    {row.product_name}
                                                                </span>
                                                            </td>
                                                            <td style={tdStyle}>{row.product_sku || "-"}</td>
                                                            <td style={tdStyle}>
                                                                <span>{row.service_name}</span>
                                                                {row.service_sku && (
                                                                    <span className="text-muted ms-1">
                                                                        {row.service_sku}
                                                                    </span>
                                                                )}
                                                                {row.service_sn && (
                                                                    <span className="text-muted ms-1">
                                                                        | SN: {row.service_sn}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td style={{ ...tdStyle, textAlign: "center" }}>
                                                                {row.issued_qty}
                                                            </td>
                                                            <td style={{ ...tdStyle, textAlign: "center" }}>
                                                                {row.already_returned_qty}
                                                            </td>
                                                            <td style={{ ...tdStyle, textAlign: "center" }}>
                                                                {row.remaining_qty}
                                                            </td>
                                                            <td style={{ ...tdStyle, minWidth: 180 }}>
                                                                <Autocomplete
                                                                    size="small"
                                                                    options={filteredOptions}
                                                                    disabled={!canReturn || submitting}
                                                                    getOptionLabel={(option) =>
                                                                        option.serial_no || ""
                                                                    }
                                                                    isOptionEqualToValue={(a, b) =>
                                                                        a.inventory_product_id ===
                                                                        b.inventory_product_id
                                                                    }
                                                                    value={selectedValue}
                                                                    onChange={(_e, selected) => {
                                                                        if (!selected) {
                                                                            updateRow(row.globalIdx, {
                                                                                checked: false,
                                                                                inventory_product_id: "",
                                                                                serial_no: "",
                                                                                warranty_date: null,
                                                                            });
                                                                            return;
                                                                        }
                                                                        updateRow(row.globalIdx, {
                                                                            checked: true,
                                                                            inventory_product_id:
                                                                                selected.inventory_product_id,
                                                                            serial_no: selected.serial_no,
                                                                            warranty_date:
                                                                                selected.warranty_date || null,
                                                                        });
                                                                    }}
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            placeholder="Select serial"
                                                                            sx={{
                                                                                "& .MuiInputBase-root": {
                                                                                    fontSize: "0.75rem",
                                                                                    backgroundColor: "#fff",
                                                                                    minHeight: 30,
                                                                                    padding: "0 4px",
                                                                                },
                                                                                "& .MuiInputBase-input": {
                                                                                    padding: "3px 6px !important",
                                                                                    fontSize: "0.75rem",
                                                                                },
                                                                                "& .MuiOutlinedInput-root": {
                                                                                    paddingTop: "0 !important",
                                                                                    paddingBottom: "0 !important",
                                                                                },
                                                                            }}
                                                                        />
                                                                    )}
                                                                />
                                                            </td>
                                                            <td style={tdStyle}>
                                                                {row.warranty_date ||
                                                                    row.available.find(
                                                                        (o) =>
                                                                            o.inventory_product_id ===
                                                                            row.inventory_product_id,
                                                                    )?.warranty_date ||
                                                                    "—"}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    ) : (
                                        /* Empty state */
                                        <div
                                            className="d-flex flex-column align-items-center justify-content-center"
                                            style={{ minHeight: 200, color: "#aaa", userSelect: "none" }}
                                        >
                                            <svg
                                                width="60"
                                                height="60"
                                                viewBox="0 0 72 72"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                style={{ marginBottom: 12, opacity: 0.2 }}
                                            >
                                                <rect
                                                    x="12"
                                                    y="8"
                                                    width="48"
                                                    height="56"
                                                    rx="4"
                                                    stroke="#888"
                                                    strokeWidth="3"
                                                />
                                                <line x1="22" y1="24" x2="50" y2="24" stroke="#888" strokeWidth="2.5" strokeLinecap="round" />
                                                <line x1="22" y1="34" x2="50" y2="34" stroke="#888" strokeWidth="2.5" strokeLinecap="round" />
                                                <line x1="22" y1="44" x2="38" y2="44" stroke="#888" strokeWidth="2.5" strokeLinecap="round" />
                                            </svg>
                                            <p style={{ fontSize: "0.88rem", marginBottom: 0 }}>
                                                No accessories available to return
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Note */}
                                <Form.Group className="mb-4" controlId="accessoryReturnNote">
                                    <Form.Label style={{ fontWeight: 600, fontSize: "0.85rem" }}>
                                        Note
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        maxLength={500}
                                        placeholder="Optional note for this return"
                                        value={note}
                                        disabled={!canReturn || submitting}
                                        onChange={(e) => setNote(e.target.value)}
                                    />
                                </Form.Group>

                                <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
                                    <CancelButton
                                        onClick={() => navigate(listingPath)}
                                        disabled={submitting}
                                        showConfirmation={false}
                                    />
                                    {canReturn && (
                                        <PrimarySubmitButton
                                            label={`Save Return${totalReturnQty ? ` (${totalReturnQty})` : ""}`}
                                            isLoading={submitting}
                                            loadingLabel="Saving..."
                                            disabled={submitting || totalReturnQty <= 0}
                                        />
                                    )}
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageContainer>
    );
};

export default ReturnAccessories;
